import { Button } from "../../components/ui/button";
import { ShoppingCart, Trash2, Minus, Plus, X, Loader2 } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "../../components/ui/dropdown-menu";
import { Badge } from "../../components/ui/badge";
import { Separator } from "../../components/ui/separator";
import { useState } from "react";
import { useLocation } from "wouter";
import { formatCurrency } from "../../lib/utils";
import { useCart } from "../../hooks/use-cart";
import { useFirebaseAuth } from "../../hooks/use-firebase-auth";

export function CartDropdown() {
  const [isOpen, setIsOpen] = useState(false);
  const [, navigate] = useLocation();
  const { user, isAuthenticated } = useFirebaseAuth();

  // Use ONLY the centralized cart hook - no separate queries
  const {
    items: cartItems,
    removeItem,
    updateQuantity,
    clearCart,
    total: cartTotal,
    itemCount,
    isLoading,
    refetchCart, // Use the refetch from the cart hook
  } = useCart();

  // Handle dropdown open/close
  const handleOpenChange = (open: boolean) => {
    setIsOpen(open);
    if (open) {
      // Only refetch when opening if needed
      // The cart hook already handles this with optimistic updates
      refetchCart();
    }
  };

  // Handle checkout - requires authentication
  const handleCheckout = () => {
    setIsOpen(false);

    if (isAuthenticated && user) {
      navigate("/cart");
    } else {
      navigate("/auth?redirect=/cart");
    }
  };

  return (
    <DropdownMenu open={isOpen} onOpenChange={handleOpenChange}>
      <DropdownMenuTrigger asChild>
        <Button
          variant="outline"
          size="icon"
          className="relative h-11 w-11"
          aria-label={itemCount > 0 ? `Open cart, ${itemCount} item${itemCount === 1 ? '' : 's'}` : 'Open cart'}
        >
          <ShoppingCart className="h-5 w-5" />
          {itemCount > 0 && (
            <Badge
              variant="destructive"
              className="absolute -top-2 -right-2 px-1.5 h-5 min-w-5"
            >
              {itemCount}
            </Badge>
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-[calc(100vw-1rem)] max-w-80 p-4">
        <div className="flex justify-between items-center mb-4">
          <h3 className="font-semibold text-lg">Your Cart</h3>
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setIsOpen(false)}
            className="h-11 w-11"
            aria-label="Close cart"
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
        {isLoading ? (
          <div className="py-8 flex justify-center">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          </div>
        ) : cartItems.length === 0 ? (
          <div className="py-8 text-center text-muted-foreground">
            <ShoppingCart className="mx-auto h-12 w-12 mb-3 opacity-50" />
            {isAuthenticated ? (
              <p>Your cart is empty</p>
            ) : (
              <div>
                <p className="mb-2">Sign in to view your cart</p>
                <Button onClick={() => navigate("/auth")} size="sm" className="min-h-11">
                  Sign In
                </Button>
              </div>
            )}
          </div>
        ) : (
          <>
            <div className="space-y-4 max-h-[300px] overflow-y-auto pr-2">
              {cartItems.map((item) => (
                <div key={item.id} className="flex space-x-3">
                  <div className="min-w-0 flex-1">
                    <h4 className="font-medium leading-tight">
                      {item.title}
                    </h4>
                    <div className="text-sm text-muted-foreground py-2">
                      {item.type}
                    </div>
                    {/* Digital resources are single-purchase — no quantity stepper. */}
                  </div>
                  <div className="flex flex-col items-end justify-between">
                    <div className="text-right font-medium">
                      {formatCurrency(
                        Number(item.price)
                      )}
                    </div>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-11 w-11 text-muted-foreground"
                      aria-label={`Remove ${item.title} from cart`}
                      onClick={() => {
                        removeItem(item.id);
                        // No manual refresh needed - optimistic updates handle this
                      }}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>

            <Separator className="my-4" />

            <div className="flex justify-between font-semibold text-lg mb-4">
              <span>Total</span>
              <span>{formatCurrency(cartTotal)}</span>
            </div>

            <div className="grid gap-2 sm:grid-cols-2">
              <Button
                variant="outline"
                className="min-h-11"
                onClick={() => {
                  clearCart();
                  // No manual refresh needed - optimistic updates handle this
                }}
              >
                Clear Cart
              </Button>
              <Button onClick={handleCheckout} className="min-h-11">Checkout</Button>
            </div>
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
